import { useMemo } from 'react';
import { identity, shuffle } from 'lodash';
import { useCompanyProfileCarouselQuery, type CompanyProfileCarouselProfileFragment } from '~api';
import { useE2EMode } from '~features/e2e-mode';

type ChildrenProps = {
  profiles: CompanyProfileCarouselProfileFragment[] | undefined;
  loading: boolean;
};

export type CarouselInputOptions = {
  cityId: number | undefined;
  categoryId: number | undefined;
};

type Props = CarouselInputOptions & {
  children: (props: ChildrenProps) => React.ReactNode;
  companyProfilesLimit: number;
  companyProfilesQueryLimit: number;
};

export const DataProvider = ({
  children,
  companyProfilesLimit,
  companyProfilesQueryLimit,
}: Props) => {
  const { data, loading } = useCompanyProfileCarouselQuery({
    variables: {
      input: {
        limit: companyProfilesQueryLimit,
        isPromoted: true,
        // conditions disabled temporarily (JOBS-9400)
        // When changing back, ensure that changes in wildcardListing.ts as reverted as well
        // cityId, categoryId
      },
    },
  });
  const e2eMode = useE2EMode();
  const profiles = useMemo(() => {
    // remove this condition once shuffling happens on backend (grep this comment line)
    const maybeShuffle = e2eMode ? (identity as typeof shuffle) : shuffle;

    return maybeShuffle(data?.CompanyProfile?.profilesCarousel.profiles)?.slice(
      0,
      companyProfilesLimit,
    );
  }, [data?.CompanyProfile?.profilesCarousel.profiles, companyProfilesLimit, e2eMode]);

  return children({ profiles, loading });
};
